/**
 * Case Management Portal navigation with shared auth token.
 * When opening CaseMgmtPortal from Healthcare Portal, we must pass the same access token
 * so CaseMgmtPortal can call the User Service API (e.g. GET /api/v1/users/entra/{id}) without
 * "invalid_token" / "The signature is invalid".
 *
 * CaseMgmtPortal expects the token in the URL (see CaseMgmtPortal frontend/src/auth/sharedAuth.ts):
 * - Fragment: #access_token=<token>
 * - Query: ?access_token=<token>
 * This param name MUST match CaseMgmtPortal's SHARED_AUTH_ACCESS_TOKEN_PARAM.
 *
 * When CaseMgmtPortal is opened in a new tab, it requests localStorage/sessionStorage via postMessage.
 * This module registers a listener that responds with all items (same keys) so CaseMgmtPortal has
 * the same storage (e.g. MSAL cache keys, app state) as Healthcare Portal.
 */
const SHARED_AUTH_ACCESS_TOKEN_PARAM = 'access_token';

/** postMessage type: CaseMgmtPortal requests storage from opener. Must match CaseMgmtPortal sharedAuth. */
const STORAGE_TRANSFER_REQUEST = 'HEALTHCARE_PORTAL_REQUEST_STORAGE';
/** postMessage type: We send storage to CaseMgmtPortal. Must match CaseMgmtPortal sharedAuth. */
const STORAGE_TRANSFER_RESPONSE = 'HEALTHCARE_PORTAL_STORAGE_TRANSFER';

import { CASE_MGMT_PORTAL_URL } from '../config/api.config';
import { CASE_MGMT_PORTAL_WINDOW_NAME } from '../constants/caseMgmtPortalWindowConstants';
import { openNamedPortalTabAfterPromise } from './crossPortalAuth.utils';
import { getMsalInstance } from '../services/msalBootstrap.service';
import { getValidAccessToken } from './tokenCache.utils';
import { mergePortalAppearanceIntoUrl } from './portalAppearance.utils';

/**
 * Build CaseMgmtPortal base URL (no trailing slash).
 */
function getCaseMgmtBaseUrl(): string {
  return String(CASE_MGMT_PORTAL_URL ?? '').replace(/\/$/, '');
}

function resolveCaseMgmtPath(path?: string): string {
  if (!path) return '';
  if (path.startsWith('/') || path.startsWith('?')) return path;
  return `/${path}`;
}

export type OpenCaseMgmtPortalOptions = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

/**
 * Get the CaseMgmtPortal URL with the current access token in the hash.
 * Use this when redirecting or opening CaseMgmtPortal so it receives the same token.
 *
 * @param path - Optional path (e.g. '/exam-workspace/123' or '/exam-workspace/123?caseId=456'). Leading slash optional. Query params (e.g. caseId) let CaseMgmtPortal load case details when user navigates back from exam-workspace.
 * @returns URL with #access_token=<token>, or URL without token if not available (fallback).
 */
export async function getCaseMgmtPortalUrlWithAuth(path?: string): Promise<string> {
  const base = getCaseMgmtBaseUrl();
  const pathPart = resolveCaseMgmtPath(path);
  const urlWithoutHash = mergePortalAppearanceIntoUrl(`${base}${pathPart}`);

  const msalInstance = getMsalInstance();
  if (!msalInstance) {
    if (import.meta.env.DEV) {
      console.warn('[CaseMgmtPortal] MSAL instance not available, opening without token');
    }
    return urlWithoutHash;
  }

  const token = await getValidAccessToken(msalInstance);
  if (!token) {
    if (import.meta.env.DEV) {
      console.warn('[CaseMgmtPortal] No access token available, opening without token');
    }
    return urlWithoutHash;
  }

  return `${urlWithoutHash}#${SHARED_AUTH_ACCESS_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}

let caseMgmtPortalWindowRef: Window | null = null;

/**
 * Open CaseMgmtPortal in the named tab with the current access token.
 * Reuses the same browser tab when Case Management is already open.
 *
 * Call synchronously from a click handler when possible: opens `about:blank` in the
 * named tab first, then navigates after the token URL resolves (popup-blocker safe).
 *
 * @param path - Optional path, or an async resolver used after the named tab is opened
 *   (so popup blockers still see a synchronous `window.open` on click).
 */
export function openCaseMgmtPortalWithAuth(
  path?: string | (() => Promise<string>),
  options?: OpenCaseMgmtPortalOptions
): void {
  openNamedPortalTabAfterPromise(
    async () => {
      const resolvedPath = typeof path === 'function' ? await path() : path;
      return getCaseMgmtPortalUrlWithAuth(resolvedPath);
    },
    {
      windowName: CASE_MGMT_PORTAL_WINDOW_NAME,
      getExistingWindow: () => caseMgmtPortalWindowRef,
      setExistingWindow: (win) => {
        caseMgmtPortalWindowRef = win;
      },
      onSuccess: options?.onSuccess,
      onError: (error) => {
        if (import.meta.env.DEV) {
          console.warn('[CaseMgmtPortal] Failed to open named tab:', error);
        }
        options?.onError?.(error);
      },
    }
  );
}

/**
 * Navigate to Case Management Portal in the current window (token in hash).
 */
export function redirectToCaseMgmtPortalWithAuth(
  path?: string | (() => Promise<string>),
  options?: OpenCaseMgmtPortalOptions
): void {
  void (async () => {
    try {
      const resolvedPath = typeof path === 'function' ? await path() : path;
      const url = await getCaseMgmtPortalUrlWithAuth(resolvedPath);
      options?.onSuccess?.();
      window.location.assign(url);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[CaseMgmtPortal] Failed to redirect:', error);
      }
      options?.onError?.(error);
    }
  })();
}

/**
 * Get CaseMgmtPortal origin for postMessage targetOrigin (no trailing slash).
 */
function getCaseMgmtPortalOrigin(): string {
  const base = String(CASE_MGMT_PORTAL_URL ?? '').replace(/\/$/, '');
  try {
    return new URL(base).origin;
  } catch {
    return base;
  }
}

/**
 * Copy all localStorage/sessionStorage into plain objects (same keys).
 */
function captureStorage(): { localStorage: Record<string, string>; sessionStorage: Record<string, string> } {
  const local: Record<string, string> = {};
  const session: Record<string, string> = {};
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key != null) local[key] = window.localStorage.getItem(key) ?? '';
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[CaseMgmtPortal] Failed to read localStorage:', e);
  }
  try {
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key != null) session[key] = window.sessionStorage.getItem(key) ?? '';
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[CaseMgmtPortal] Failed to read sessionStorage:', e);
  }
  return { localStorage: local, sessionStorage: session };
}

/**
 * Register a message listener that sends all localStorage and sessionStorage to CaseMgmtPortal
 * when it requests them (opener → this window). Use inside useEffect and call the returned
 * unsubscribe on cleanup (same handler reference as removeEventListener requires).
 *
 * @returns Unsubscribe, or a no-op if window is unavailable (e.g. SSR).
 */
export function registerStorageTransferListener(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const caseMgmtOrigin = getCaseMgmtPortalOrigin();
  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== caseMgmtOrigin) return;
    if (event.data?.type !== STORAGE_TRANSFER_REQUEST) return;
    const source = event.source;
    if (!source || typeof (source as Window).postMessage !== 'function') return;
    try {
      const { localStorage: localData, sessionStorage: sessionData } = captureStorage();
      // source.postMessage throws when COOP from login.live.com (Cross-Origin-Opener-Policy:
      // same-origin) severs the window reference after logoutRedirect. CMP already received
      // the access token via URL hash so auth still works; storage sync is best-effort.
      (source as Window).postMessage(
        {
          type: STORAGE_TRANSFER_RESPONSE,
          localStorage: localData,
          sessionStorage: sessionData,
        },
        caseMgmtOrigin
      );
      if (import.meta.env.DEV) {
        console.log(
          '[CaseMgmtPortal] Sent storage to CaseMgmtPortal:',
          Object.keys(localData).length,
          'localStorage,',
          Object.keys(sessionData).length,
          'sessionStorage'
        );
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[CaseMgmtPortal] Failed to send storage to CaseMgmtPortal:', e);
    }
  };
  window.addEventListener('message', onMessage);
  return () => {
    window.removeEventListener('message', onMessage);
  };
}
